import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Settings, Edit3, Save, X, Upload, Image as ImageIcon, Zap, CheckCircle, AlertCircle } from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'
import { settingsService } from '../../services/api'

const SettingsTab = () => {
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [editedSettings, setEditedSettings] = useState({})
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)

  // Image compression state
  const [compressLoading, setCompressLoading] = useState(false)
  const [compressResult, setCompressResult] = useState(null)
  const [compressError, setCompressError] = useState(null)

  // Fetch settings using React Query
  const { data: settings, isLoading: settingsLoading, error: settingsError } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => settingsService.getSettings(),
    retry: false,
  })

  const updateSettingsMutation = useMutation({
    mutationFn: (settingsData) => {
      // Create FormData for file upload
      const formData = new FormData()
      Object.keys(settingsData).forEach(key => {
        if (key === 'logo' && logoFile) {
          formData.append('logo', logoFile)
        } else if (key !== 'logo') {
          formData.append(key, settingsData[key] || '')
        }
      })
      
      return fetch('/api/settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      }).then(res => res.json())
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-settings'])
      setIsEditing(false)
      setEditedSettings({})
      setLogoFile(null)
      setLogoPreview(null)
    }
  })

  const onEditSettings = () => {
    setEditedSettings(settings || {})
    setLogoPreview(settings?.logo ? `/uploads/settings/${settings.logo}` : null)
    setIsEditing(true)
  }

  const onSaveSettings = () => {
    updateSettingsMutation.mutate(editedSettings)
  }

  const onCancelEdit = () => {
    setIsEditing(false)
    setEditedSettings({})
    setLogoFile(null)
    setLogoPreview(null)
  }

  const handleInputChange = (field, value) => {
    setEditedSettings(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleLogoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setLogoFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setLogoPreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleCompressImages = async () => {
    setCompressLoading(true)
    setCompressResult(null)
    setCompressError(null)
    try {
      const res = await fetch('/api/admin/compress-images', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      const data = await res.json()
      if (data.success) {
        setCompressResult(data)
      } else {
        setCompressError(data.message || 'فشل الضغط')
      }
    } catch (err) {
      setCompressError(err.message)
    } finally {
      setCompressLoading(false)
    }
  }
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">إعدادات المتجر</h2>
      <div className="bg-white shadow rounded-lg border">
        <div className="px-6 py-4">
          {settingsLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : settingsError ? (
            <div className="text-center py-8">
              <p className="text-red-600">خطأ في تحميل الإعدادات: {settingsError.message}</p>
            </div>
          ) : settings ? (
            isEditing ? (
              // Edit Form
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">معلومات المتجر</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700">اسم المتجر:</label>
                        <input
                          type="text"
                          value={editedSettings.store_name || ''}
                          onChange={(e) => handleInputChange('store_name', e.target.value)}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">الوصف:</label>
                        <textarea
                          value={editedSettings.store_description || ''}
                          onChange={(e) => handleInputChange('store_description', e.target.value)}
                          rows={3}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">البريد الإلكتروني:</label>
                        <input
                          type="email"
                          value={editedSettings.email || ''}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">العنوان:</label>
                        <input
                          type="text"
                          value={editedSettings.address || ''}
                          onChange={(e) => handleInputChange('address', e.target.value)}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">شعار المتجر:</label>
                        <div className="mt-1 space-y-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          />
                          {logoPreview && (
                            <div className="mt-2">
                              <img
                                src={logoPreview}
                                alt="Logo Preview"
                                className="w-24 h-24 object-contain border border-gray-300 rounded-lg"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">معلومات التواصل</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700">الهاتف:</label>
                        <input
                          type="text"
                          value={editedSettings.phone_number || ''}
                          onChange={(e) => handleInputChange('phone_number', e.target.value)}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">WhatsApp:</label>
                        <input
                          type="text"
                          value={editedSettings.whatsapp_number || ''}
                          onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t flex space-x-3">
                  <button 
                    onClick={onSaveSettings}
                    disabled={updateSettingsMutation.isLoading}
                    className="btn btn-primary"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {updateSettingsMutation.isLoading ? 'حفظ...' : 'حفظ الإعدادات'}
                  </button>
                  <button 
                    onClick={onCancelEdit}
                    className="btn btn-outline"
                  >
                    <X className="h-4 w-4 mr-2" />
                    إلغاء
                  </button>
                </div>
              </div>
            ) : (
              // Display Mode
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Store Information</h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm font-medium text-gray-700">Store Name:</span>
                        <p className="text-sm text-gray-900">{settings.store_name}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">Description:</span>
                        <p className="text-sm text-gray-900">{settings.store_description}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">Email:</span>
                        <p className="text-sm text-gray-900">{settings.email || 'Not set'}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">العنوان:</span>
                        <p className="text-sm text-gray-900">{settings.address || 'غير محدد'}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">Store Logo:</span>
                        {settings.logo ? (
                          <div className="mt-2">
                            <img
                              src={`/uploads/settings/${settings.logo}`}
                              alt="Store Logo"
                              className="w-24 h-24 object-contain border border-gray-300 rounded-lg"
                            />
                          </div>
                        ) : (
                          <div className="mt-2 w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm font-medium text-gray-700">Phone:</span>
                        <p className="text-sm text-gray-900">{settings.phone_number || 'Not set'}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">WhatsApp:</span>
                        <p className="text-sm text-gray-900">{settings.whatsapp_number || 'Not set'}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <button 
                    onClick={onEditSettings}
                    className="btn btn-primary"
                  >
                    <Edit3 className="h-4 w-4 mr-2" />
                    تعديل الإعدادات
                  </button>
                </div>
              </div>
            )
          ) : (
            <div className="text-center py-8">
              <Settings className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Settings Found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Configure your store settings to get started.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Image Compression ── */}
      <div className="bg-white shadow rounded-lg border">
        <div className="px-6 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-5 w-5 text-yellow-500" />
            <h3 className="text-lg font-semibold text-gray-900">ضغط صور المنتجات</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            يقوم بضغط جميع صور المنتجات الموجودة على السيرفر تلقائيًا (حد أقصى 1400 بكسل عرض، جودة 80%).
            الصور الجديدة تُضغط تلقائيًا عند الرفع.
          </p>

          <button
            onClick={handleCompressImages}
            disabled={compressLoading}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-300 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Zap className="h-4 w-4" />
            {compressLoading ? 'جاري الضغط...' : 'ضغط جميع الصور الحالية'}
          </button>

          {compressLoading && (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
              <LoadingSpinner size="sm" />
              <span>جاري معالجة الصور، قد يستغرق هذا بضع ثوانٍ...</span>
            </div>
          )}

          {compressResult && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">{compressResult.message}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div className="bg-white rounded p-2 text-center border border-green-100">
                  <div className="text-xl font-bold text-green-700">{compressResult.stats.processed}</div>
                  <div className="text-gray-500">مضغوطة</div>
                </div>
                <div className="bg-white rounded p-2 text-center border border-green-100">
                  <div className="text-xl font-bold text-gray-600">{compressResult.stats.skipped}</div>
                  <div className="text-gray-500">تخطت</div>
                </div>
                <div className="bg-white rounded p-2 text-center border border-green-100">
                  <div className="text-xl font-bold text-blue-700">{compressResult.stats.totalSavedMB} MB</div>
                  <div className="text-gray-500">وفّرنا</div>
                </div>
                <div className="bg-white rounded p-2 text-center border border-green-100">
                  <div className="text-xl font-bold text-red-500">{compressResult.stats.errors}</div>
                  <div className="text-gray-500">أخطاء</div>
                </div>
              </div>
              {compressResult.errors && compressResult.errors.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs text-red-600 cursor-pointer">عرض الأخطاء</summary>
                  <ul className="mt-1 text-xs text-red-500 space-y-1">
                    {compressResult.errors.map((e, i) => (
                      <li key={i}>{e.filename}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {compressError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{compressError}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SettingsTab
